import { useEffect, useState } from "react";

import {
  ArrowButton,
  Select,
  TableMetadata,
  TableStateWithFilters,
  useTable,
  Label,
  ColumnSortEnum,
} from "@king-ict/eupisi-ui/components";
import { PagedResult, ExportTypeEnum } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { getFaqItemListExport, searchItem } from "../../api/Faq/FaqApi";
import {
  ContentArea,
  SearchCard,
  ToolbarTop,
  ListTable,
} from "../../components";
import { FaqItemSort, Item, ItemSearch } from "../../models/Faq";
import { useGetFaqGroupListQuery } from "../../queries/codeBookQuery";
import { faqItemDetailUrl, faqItemEditUrl } from "../../routes/routes";
import { TableUtils } from "../../utils";

export function FaqItemList() {
  const { t } = useTranslation();
  const history = useHistory();
  //STORAGE KEY
  const storageKey = "FaqSearchTableState";
  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const createInitialTableState = () => {
    return TableUtils.CreateInitialTableState<ItemSearch>(
      storageKey,
      TableUtils.tableDefaultState(FaqItemSort.Group, ColumnSortEnum.DESC)
    );
  };
  const [initialTableState] = useState<TableStateWithFilters<ItemSearch>>(() =>
    createInitialTableState()
  );
  //INIT SEARCH FILTER
  const createInitialItemSearch = (): ItemSearch => {
    return {
      groupId: initialTableState.groupId,
    };
  };
  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<Item>();
  const [tableData, setTableData] =
    useState<PagedResult<Item>>(defaultTableData);
  //SET SEARCH FILTERS
  const [itemSearch, setItemSearch] = useState<ItemSearch>(() =>
    createInitialItemSearch()
  );
  const [tableSearch, setTableSearch] = useState<ItemSearch>(() =>
    createInitialItemSearch()
  );
  const [filterChanged, setFilterChanged] = useState<boolean>(false);
  const [clearFilter, setClearFilter] = useState<boolean>(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<Item> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "group",
        header: t("common.faq.group"),
        accessor: (item: Item) => {
          return <div>{item.group?.name}</div>;
        },
        sortBy: FaqItemSort.Group,
      },
      {
        columnKey: "question",
        header: t("common.faq.question"),
        accessor: (item: Item) => {
          return <div>{item.question}</div>;
        },
        sortBy: FaqItemSort.Question,
      },
      {
        columnKey: "status",
        header: t("common.faq.active"),
        accessor: (item: Item) => {
          return (
            <div className={"d-flex align-center"}>
              <Label
                color={item.status ? "success" : "noted-2"}
                className="text-center"
                outline={false}
              >
                {item.status ? t("common.yes") : t("common.no")}
              </Label>
            </div>
          );
        },
        sortBy: FaqItemSort.Status,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: Item) => {
          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={t("common.details")}
                onClick={() => handleDetailsClicked(item.id)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] = useTable({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: initialTableState.page,
    paginationPageSize: initialTableState.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: initialTableState.order,
      sortBy: initialTableState.sortBy,
    },
  });
  //FILTER DATA
  const { data: groups } = useGetFaqGroupListQuery();

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemSearch]);

  useEffect(() => {
    (async () => {
      var request = TableUtils.GetSearchRequest<ItemSearch>(
        tableSearch,
        tableState
      );
      var itemList = await searchItem(request);
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleDetailsClicked = (id?: number) => {
    if (id) {
      TableUtils.SetSessionStorageItem<ItemSearch>(
        storageKey,
        tableSearch,
        tableState
      );
      history.push(faqItemDetailUrl(id!));
    }
  };
  const handleNewItemClicked = () => {
    history.push(faqItemEditUrl(0));
  };
  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getFaqItemListExport(itemSearch, exportType);
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };

  const handleGroupChange = (e: any) => {
    const groupId = Number.parseInt(e.target.value);
    const newItemSearch: ItemSearch = {
      ...itemSearch,
      groupId: groupId,
    };
    setItemSearch(newItemSearch);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"show"}
        showBack={"hide"}
        onClickAdd={handleNewItemClicked}
        pageTitle={t("navigation.faqList")}
      ></ToolbarTop>
      <SearchCard
        onClickSearch={() => {
          setTableSearch(itemSearch);
          setFilterChanged(false);
          search();
        }}
        onClickClearSearch={() => {
          setClearFilter(true);
          setItemSearch({ groupId: undefined });
          setTableSearch({ groupId: undefined });
          search();
        }}
        isFilterChanged={filterChanged}
      >
        <Row>
          <Col md={6}>
            <Select
              label={t("common.faq.group")}
              name={nameof<typeof itemSearch>("groupId")}
              value={itemSearch.groupId}
              onChange={handleGroupChange}
              selectData={codeBookToSelectData(groups)}
              emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable
        tableProps={tableProps}
        onClickDownload={(exportType: ExportTypeEnum) =>
          handleDownloadClicked(exportType)
        }
        showDownload
      />
    </ContentArea>
  );
}
